import { motion } from "framer-motion";
export type BLevel = "B0" | "B1" | "B2";

/** Number of lymphocytes drawn with a convoluted (cerebriform) nucleus. Conceptual only. */
const ATYPICAL: Record<BLevel, number> = {
  B0: 1,
  B1: 5,
  B2: 14
};
export function BloodField({
  level
}: {
  level: BLevel;
}) {
  const atypical = ATYPICAL[level];
  return <svg viewBox="0 0 300 316" className="block h-auto w-full" role="img" aria-label={`Conceptual blood film, ${level}`} data-id="element-324" data-mp="3vjn5jk9nzm3">
      <defs data-id="element-325" data-mp="8tl33vmebitc">
        <clipPath id="field-clip" data-id="element-326" data-mp="8w7btyl0w4d5">
          <circle cx={150} cy={150} r={138} data-id="element-327" data-mp="ds68e9gi8son" />
        </clipPath>
      </defs>
      <circle cx={150} cy={150} r={140} fill="#fbf7f6" stroke="var(--color-rule-strong)" strokeWidth={1} data-id="element-328" data-mp="huj9ulo7y4fm" />
      <g clipPath="url(#field-clip)" data-id="element-329" data-mp="mtdsnnm6rszq">
        {RBCS.map(([x, y, rot], i) => <g key={`r${i}`} transform={`rotate(${rot} ${x} ${y})`} data-id="element-330" data-mp="mr9or5oaetzg" data-mpi={i}>
            <ellipse cx={x} cy={y} rx={10} ry={9} fill="#ecc9c6" data-id="element-331" data-mp="2jngzy9qm2iwv" />
            <ellipse cx={x} cy={y} rx={4.5} ry={4} fill="#f5dedb" data-id="element-332" data-mp="2ok7fmvyi7chr" />
          </g>)}
        {LYMPHS.map(([x, y], i) => {
        const isAtypical = ORDER.indexOf(i) < atypical;
        return <g key={`l${i}`} data-id="element-333" data-mp="1amu9hhgvr99t" data-mpi={i}>
              <circle cx={x} cy={y} r={12} fill="#eef1f6" stroke={isAtypical ? "#1e3a5f" : "#b9c2ce"} strokeWidth={isAtypical ? 1.2 : 0.8} data-id="element-334" data-mp="1amlxdrsl07zo" />
              <motion.circle cx={x} cy={y} r={8.6} fill="#7d8ea6" initial={false} animate={{
            opacity: isAtypical ? 0 : 1
          }} transition={{
            duration: 0.6,
            delay: ORDER.indexOf(i) % 14 * 0.04,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-335" data-mp="11ngvqclq3yja" />
              <motion.path d={cerebriform(x, y, i)} fill="#1e3a5f" initial={false} animate={{
            opacity: isAtypical ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: ORDER.indexOf(i) % 14 * 0.04,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-336" data-mp="1pb8dvwty35sh" />
              <motion.path d={groove(x, y, i)} fill="none" stroke="#eef1f6" strokeWidth={1.1} strokeLinecap="round" initial={false} animate={{
            opacity: isAtypical ? 0.9 : 0
          }} transition={{
            duration: 0.6,
            delay: ORDER.indexOf(i) % 14 * 0.04
          }} data-id="element-337" data-mp="90mhvyjevxes" />
            </g>;
      })}
      </g>
      <g fontFamily="Inter, sans-serif" fontSize={10} fill="#5f656e" data-id="element-338" data-mp="3nu3diexvufki">
        <circle cx={86} cy={308} r={4} fill="#7d8ea6" data-id="element-339" data-mp="3nss7s9kvmxqr" />
        <text x={94} y={311} data-id="element-340" data-mp="3sm3jop4jq4pj">Lymphocyte</text>
        <circle cx={166} cy={308} r={4} fill="#1e3a5f" data-id="element-341" data-mp="3sn65wjqxbiz4" />
        <text x={174} y={311} data-id="element-342" data-mp="3ev846tzjkpcz">Atypical (Sézary) cell</text>
      </g>
    </svg>;
}

// ---- deterministic geometry ----

function rng(seed: number) {
  return () => {
    seed |= 0;
    seed = seed + 0x6d2b79f5 | 0;
    let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function scatter(count: number, minDist: number, seed: number, avoid: [number, number][] = [], avoidDist = 0) {
  const r = rng(seed);
  const pts: [number, number][] = [];
  let guard = 0;
  while (pts.length < count && guard < 6000) {
    guard++;
    const a = r() * Math.PI * 2;
    const d = Math.sqrt(r()) * 132;
    const p: [number, number] = [150 + Math.cos(a) * d, 150 + Math.sin(a) * d];
    if (pts.some(([x, y]) => Math.hypot(x - p[0], y - p[1]) < minDist)) continue;
    if (avoid.some(([x, y]) => Math.hypot(x - p[0], y - p[1]) < avoidDist)) continue;
    pts.push(p);
  }
  return pts;
}
const LYMPHS = scatter(22, 34, 7);
const RBCS: [number, number, number][] = scatter(90, 19, 21, LYMPHS, 21).map(([x, y], i) => [x, y, i * 37 % 180]);

/** Fixed order in which lymphocytes become atypical, so higher categories add to lower ones. */
const ORDER = (() => {
  const r = rng(99);
  const idx = LYMPHS.map((_, i) => i);
  for (let i = idx.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [idx[i], idx[j]] = [idx[j], idx[i]];
  }
  return idx;
})();
function cerebriform(cx: number, cy: number, seed: number) {
  const pts: string[] = [];
  const phase = seed * 0.7;
  for (let k = 0; k <= 48; k++) {
    const t = k / 48 * Math.PI * 2;
    const rr = 8.4 * (1 + 0.12 * Math.sin(5 * t + phase) + 0.06 * Math.sin(9 * t + phase * 2));
    pts.push(`${(cx + Math.cos(t) * rr).toFixed(2)} ${(cy + Math.sin(t) * rr).toFixed(2)}`);
  }
  return `M${pts.join(" L")} Z`;
}
function groove(cx: number, cy: number, seed: number) {
  const a = seed * 0.9;
  const dx = Math.cos(a) * 5;
  const dy = Math.sin(a) * 5;
  return `M${cx - dx} ${cy - dy} Q ${cx + dy * 0.6} ${cy - dx * 0.6} ${cx + dx} ${cy + dy}`;
}